import { registerFragment } from '@neptune/cx/registry';
import './styles.scss';

const YT_RE    = /^(?:https?:)?\/\/(?:www\.|m\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const VIMEO_RE = /^(?:https?:)?\/\/(?:www\.|player\.)?vimeo\.com\/(?:[^/?#]+\/)*(\d+)/;

let ytReady: Promise<any> | null = null;
let vimeoReady: Promise<void> | null = null;

function formatDuration(seconds: number): string {
if (!seconds || isNaN(seconds)) return '';
const hrs = Math.floor(seconds / 3600);
const mins = Math.floor((seconds % 3600) / 60);
const secs = Math.floor(seconds % 60);

const formattedSecs = secs.toString().padStart(2, '0');

if (hrs > 0) {
    const formattedMins = mins.toString().padStart(2, '0');
    return `${hrs}:${formattedMins}:${formattedSecs}`;
}
return `${mins}:${formattedSecs}`;
}

registerFragment('chroa-video-slide', (fragmentEl) => {
    const slideEl = fragmentEl.querySelector('.chroa-video-slide') as HTMLElement;
    if (!slideEl) return;

    const img = fragmentEl.querySelector('.chroa-video-slide__poster') as HTMLImageElement;
    const btn = fragmentEl.querySelector<HTMLButtonElement>('[data-chroa-video-play]');
    const box = fragmentEl.querySelector<HTMLElement>('.chroa-video-slide__video');
    const durationEl = fragmentEl.querySelector<HTMLElement>('[data-chroa-video-duration]');

    if (!img || !box) return;

    const url = (slideEl.dataset.videoUrl || '').trim();
    const parsed = parseUrl(url);
    const title = fragmentEl.querySelector('.chroa-video-slide__title')?.textContent?.trim() || '';

    let activePlayer: any = null;
    let activeProvider: 'youtube' | 'vimeo' | null = null;

    let hasCustomThumbnail = false;
    let customThumbnailSrc = '';
    if (slideEl.querySelector('.chroa-video-slide__poster--custom-thumbnail')) {
        hasCustomThumbnail = true;
    }
    let slideElPoster = <HTMLImageElement>slideEl.querySelector('.chroa-video-slide__poster--custom-thumbnail');
    if(slideElPoster) {
        customThumbnailSrc = slideElPoster.src;
    }

    if (parsed?.provider === 'youtube') {
        img.src = !hasCustomThumbnail ? `https://i.ytimg.com/vi/${parsed.id}/hqdefault.jpg` : customThumbnailSrc;
        img.style.display = '';

        if (durationEl) {
        loadYouTube().then((YT) => {
            const tempDiv = document.createElement('div');
            tempDiv.style.display = 'none';
            fragmentEl.appendChild(tempDiv);

            const tempPlayer = new YT.Player(tempDiv, {
            videoId: parsed.id,
            playerVars: { autoplay: 0, mute: 1, controls: 0, showinfo: 0, rel: 0 },
            events: {
                onReady: (event: any) => {
                const duration = event.target.getDuration();
                if (duration > 0) {
                    durationEl.textContent = formatDuration(duration);
                    durationEl.style.display = '';
                }
                event.target.destroy();
                tempDiv.remove();
                }
            }
            });
        });
        }
    } else if (parsed?.provider === 'vimeo') {
        fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${parsed.id}`)
        .then((response) => response.json())
        .then((data) => {
            if (data && data.thumbnail_url) {
            img.src = !hasCustomThumbnail ? data.thumbnail_url : customThumbnailSrc;
            img.style.display = '';
            }
            if (durationEl && data && data.duration) {
            durationEl.textContent = formatDuration(data.duration);
            durationEl.style.display = '';
            }
        })
        .catch((err) => {
            if (img.src.startsWith('data:')) img.style.display = 'none';
        });
    } else if (img.src.startsWith('data:')) {
        img.style.display = 'none';
    }

  if (btn) {
    btn.disabled = !parsed;
    if (parsed) {
      btn.removeAttribute('disabled');
      btn.setAttribute('aria-label', `Play video: ${title || 'video'}`);
    } else {
      btn.setAttribute('disabled', '');
      btn.setAttribute('aria-label', 'Video unavailable');
    }
  }

  if (btn && parsed) {
    let isPlaying = false;

    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      if (isPlaying) return;
      isPlaying = true;

      box.replaceChildren();

      try {
        if (parsed.provider === 'youtube') {
          const YT = await loadYouTube();
          const host = document.createElement('div');
          host.className = 'chroa-video-slide__yt-host';
          box.appendChild(host);

          activePlayer = new YT.Player(host, {
            videoId: parsed.id,
            playerVars: { autoplay: 1, enablejsapi: 1, rel: 0, modestbranding: 1, playsinline: 1 },
            events: {
              onReady: (event: any) => {
                const iframe = event.target.getIframe();
                iframe.title = title || 'YouTube video';
                iframe.focus({ preventScroll: true });
              },
            },
          });
          activeProvider = 'youtube';
        } else {
          await loadVimeo();
          const iframe = document.createElement('iframe');
          iframe.src = `https://player.vimeo.com/video/${parsed.id}?autoplay=1&playsinline=1`;
          iframe.allow = 'autoplay; fullscreen; picture-in-picture';
          iframe.setAttribute('allowfullscreen', '');
          iframe.title = title || 'Vimeo video';
          box.appendChild(iframe);

          if ((window as any).Vimeo?.Player) {
            activePlayer = new (window as any).Vimeo.Player(iframe);
          }
          activeProvider = 'vimeo';
          iframe.focus({ preventScroll: true });
        }
      } catch (err) {
        isPlaying = false;
        activePlayer = null;
        activeProvider = null;
      }
    });
  }

  const parentSlider = slideEl.closest('.splide');
  const pausePreviousEnabled = parentSlider?.getAttribute('data-pause-previous') === 'true';

  if (pausePreviousEnabled) {
    const targetSlideNode = slideEl.parentElement?.closest('.splide__slide') || slideEl;
    let wasVisible = targetSlideNode.classList.contains('is-visible');

    const visibilityObserver = new MutationObserver(() => {
      const isVisible = targetSlideNode.classList.contains('is-visible');

      if (wasVisible && !isVisible) {
        if (activePlayer) {
          if (activeProvider === 'youtube' && typeof activePlayer.pauseVideo === 'function') {
            activePlayer.pauseVideo();
          } else if (activeProvider === 'vimeo' && typeof activePlayer.pause === 'function') {
            activePlayer.pause();
          }
        }
      }
      wasVisible = isVisible;
    });

    visibilityObserver.observe(targetSlideNode, { attributes: true, attributeFilter: ['class'] });
  }
});

function parseUrl(url: string): { provider: 'youtube' | 'vimeo'; id: string } | null {
  let m = url.match(YT_RE);
  if (m) return { provider: 'youtube', id: m[1] };
  m = url.match(VIMEO_RE);
  return m ? { provider: 'vimeo', id: m[1] } : null;
}

function loadYouTube(): Promise<any> {
  return (ytReady ??= new Promise((resolve) => {
    const w = window as any;
    if (w.YT?.Player) return resolve(w.YT);
    const prev = w.onYouTubeIframeAPIReady;
    w.onYouTubeIframeAPIReady = () => {
      if (typeof prev === 'function') prev();
      resolve(w.YT);
    };
    addScript('https://www.youtube.com/iframe_api');
  }));
}

function loadVimeo(): Promise<void> {
  return (vimeoReady ??= new Promise((resolve, reject) => {
    if ((window as any).Vimeo?.Player) return resolve();
    const s = addScript('https://player.vimeo.com/api/player.js');
    s.onload = () => resolve();
    s.onerror = reject;
  }));
}

function addScript(src: string): HTMLScriptElement {
  const s = document.createElement('script');
  s.src = src;
  s.async = true;
  document.head.appendChild(s);
  return s;
}
